<script lang="ts" setup>
import { useMarketingStore } from '@/store/marketing.store';
import { useModalStore } from '@/store/modal.store';
import { ISignUpLink } from '@/types';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import DefaultBtn from "@/components/block/DefaultBtn.vue";
import * as XLSX from 'xlsx';

import CopyButton from '@/components/block/CopyButton.vue';
import { getDownloadURL, getStorage, ref as storageRef, uploadBytes } from 'firebase/storage';
import { useNotificationsStore } from '@/store/notifications.store';

const props = defineProps<{
	closeSignal?: boolean
}>();

watch(() => props.closeSignal, () => {
	if (props.closeSignal) {
		close();
	}
});

const modalStore = useModalStore();
const marketingStore = useMarketingStore();
const route = useRoute();
const notificationsStore = useNotificationsStore();

const linkId = computed<number>(() => +(route.params.linkId || '0') || 0);
const originLink = computed<ISignUpLink | undefined>(() => linkId.value && marketingStore.signUpLinksMap.get(linkId.value) || undefined);

const linkFileUrl = computed<string | undefined>(() => {
	if (originLink.value?.fileUrl) {
		return originLink.value.fileUrl;
	}
	return undefined;
});


const loading = ref<boolean>(false);
const downloading = ref<boolean>(false);
const renameLink = (): void => {
	modalStore.closeModal();
};

const close = (): void => {
	if (!loading.value) {
		modalStore.closeModal();
	}
};

async function downloadCsv() {
	if (!originLink.value?.fileUrl) return;

	downloading.value = true;

	try {
		const csvContent = await marketingStore.getCSVContent(linkId.value);
		console.log('CSV Content:', csvContent);

		if (!csvContent || csvContent.length === 0) {
			notificationsStore.error('No data found in the file');
			return;
		}

		const worksheet = XLSX.utils.json_to_sheet(csvContent);
		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, 'Subscribers');

		const xlsxBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

		const blob = new Blob([xlsxBuffer], {
			type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
		});
		const url = URL.createObjectURL(blob);

		const link = document.createElement('a');
		link.href = url;
		link.download = 'FA subscriber upload template.xlsx';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);

		URL.revokeObjectURL(url);

		notificationsStore.success('Excel file downloaded successfully');
	} catch (error) {
		console.error('Error downloading CSV:', error);
		notificationsStore.error('Error downloading CSV');
	} finally {
		downloading.value = false;
	}
}

function isXlsxFile(file: File): boolean {
	const xlsxMimeTypes = [
		'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		'application/vnd.ms-excel.sheet.macroEnabled.12',
		'application/vnd.ms-excel',
		'application/octet-stream'
	];
	return xlsxMimeTypes.includes(file.type);
}

function xlsxToCsv(xlsxFile: File): Promise<File> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();

		reader.onload = function (e: ProgressEvent<FileReader>) {
			if (!e.target || !e.target.result) {
				reject(new Error('Error reading file'));
				return;
			}

			const data = new Uint8Array(e.target.result as ArrayBuffer);
			const workbook = XLSX.read(data, { type: 'array' });
			const sheetName = workbook.SheetNames[0];
			const worksheet = workbook.Sheets[sheetName];
			const csv = XLSX.utils.sheet_to_csv(worksheet);

			const blob = new Blob([csv], { type: 'text/csv' });
			const csvFile = new File([blob], 'converted.csv', { type: 'text/csv' });
			resolve(csvFile);
		};

		reader.onerror = function (error) {
			reject(error);
		};

		reader.readAsArrayBuffer(xlsxFile);
	});
}

const fileLoading = ref<boolean>(false);

async function uploadFile(file: File) {
	// if (!originLink.value?.fileUrl) return;

	const storage = getStorage();
	fileLoading.value = true;

	try {
		// Firebase Storage URL example: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{path}?alt=...
		let url: string = '';

		await marketingStore.getFileUrlLink(linkId.value)
			.then((fileUrl) => {
				url = fileUrl;
			})

		if (!url) {
			throw new Error('Failed to get file URL');
		}

		const urlObject = new URL(url);

		const pathSegments = urlObject.pathname.split('/o/');
		if (pathSegments.length < 2) {
			throw new Error('Failed to extract file path from URL');
		}

		const filePath = decodeURIComponent(pathSegments[1].split('?')[0]);

		console.log('Overwrite the file at the path:', filePath);

		const storageRefAlias = storageRef(storage, filePath);

		await uploadBytes(storageRefAlias, file);

		const downloadURL = await getDownloadURL(storageRefAlias);

		marketingStore.updateFileUrlSignUpLink(linkId.value, downloadURL);

		if (originLink.value) {
			originLink.value.fileUrl = downloadURL;
			marketingStore.signUpLinksMap.set(linkId.value, { ...originLink.value, fileUrl: downloadURL });
		}

		notificationsStore.success('The template file has been successfully updated');

		isFileUploaded.value = true;

		console.log('File successfully overwritten, new URL:', downloadURL);

	} catch (error) {
		console.error('Error when uploading a file:', error);

		notificationsStore.error('Failed to update the template file');
	} finally {
		fileLoading.value = false;
	}
}


function isCsvFile(file: File): boolean {
	const csvMimeTypes = [
		'text/csv',
		'application/csv',
		'text/plain'
	];

	const csvExtensions = [
		'.csv'
	];

	const fileTypeMatches = csvMimeTypes.includes(file.type);
	const fileNameMatches = csvExtensions.some(extension => file.name.endsWith(extension));

	return fileTypeMatches || fileNameMatches;
}

function handleFileChange(event: Event) {
	const input = event.target as HTMLInputElement;
	const selectedFile = input.files?.[0] || null;
	input.value = '';

	if (selectedFile) {
		isFileUploaded.value = false;

		if (isXlsxFile(selectedFile)) {
			fileLoading.value = true;
			xlsxToCsv(selectedFile)
				.then((result) => {
					uploadFile(result);
				})
				.catch(err => {
					console.error(err);
					notificationsStore.error('Upload File Failed');
					fileLoading.value = false
				})

		} else if (isCsvFile(selectedFile)) {
			uploadFile(selectedFile);
		} else {
			notificationsStore.error('Поддерживаются только файлы CSV и XLSX');
		}
	}
}
const fileInput = ref<HTMLInputElement | null>(null);

function handleFileUpload() {
	fileInput.value?.click();
}

const isFileUploaded = ref<boolean>(false);

</script>

<template>
	<div class="modal modal--active">
		<div class="modal__body">
			<div class="modal__header">
				<p class="modal__subtitle">
					<span class="material-symbols-outlined">draft</span>
					<span>Change List</span>
				</p>
				<button @click="modalStore.closeModal" class="modal__close">
					<span class="material-symbols-outlined">close</span>
				</button>
			</div>
			<div class="modal__main">
				<form @submit.prevent="renameLink">
					<p class="modal__title">Edit Customer List</p>
					<p class="modal__text">You can download the current file to edit it.</p>
					<button class="upload__btn upload__btn--mb" :class="{ 'upload__btn--loading': downloading }"
						@click.prevent="downloadCsv">
						<span class="material-symbols-outlined">
							download
						</span>
						<span v-if="!downloading">Download current file</span>
						<span v-else>Downloading</span>
					</button>

					<input type="file" ref="fileInput" accept=".csv,.xlsx" style="display: none;"
						@change="handleFileChange" />
					<p class="modal__text">Upload a new or corrected file</p>

					<div class="bottom__wrap">
						<button v-if="!fileLoading" type="button" class="upload__btn" @click="handleFileUpload">
							<span class="material-symbols-outlined" :class="{ 'uploaded-file': isFileUploaded }">
								{{ isFileUploaded ? 'task_alt' : 'upload' }}
							</span>
							<span :class="{ 'uploaded-file': isFileUploaded }">
								{{ isFileUploaded ? 'File Uploaded' : 'Upload new file' }}
							</span>
						</button>
						<button v-if="fileLoading" type="button" class="upload__btn upload__btn--loading" disabled>
							<span class="material-symbols-outlined">hourglass_empty</span>
							<span>Uploading...</span>
						</button>
					</div>

					<DefaultBtn class="login__btn" :loading text="Continue" type="submit" />
				</form>
			</div>
		</div>
	</div>
</template>

<style scoped lang="scss">
.modal__title {
	font-size: 22px;
	font-weight: 700;
	margin-bottom: 30px;
}

.modal__text {
	margin-bottom: 16px;
}

.login__btn {
	max-width: 333px;
	box-shadow: none;
	margin-top: 30px;
}

.upload__btn {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 10px 16px;
	border: 1px solid #d1d5db;
	border-radius: 6px;
	background: white;
	cursor: pointer;
	transition: all 0.2s;
	margin: 0 auto;

	&--mb {
		margin-bottom: 20px;
	}
}

.upload__btn:hover:not(:disabled) {
	border-color: #2563eb;
	background: #f8fafc;
}

.upload__btn--loading {
	opacity: 0.6;
	cursor: not-allowed;
}

.uploaded-file {
	color: #059669 !important;
}

.bottom__wrap {
	margin-bottom: 20px;
}
</style>